import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Eye, Archive, Trash2, RotateCcw, Search, Inbox, AlertTriangle, CalendarX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const TYPE_OPTIONS = [
  ["", "Tutte le tipologie"],
  ["contact", "Messaggi di contatto"],
  ["booking", "Richieste di appuntamento"],
  ["estimate", "Richieste di preventivo"],
  ["inquiry", "Prezzo esclusivo (Shop)"],
  ["quote", "Preventivi"],
  ["newsletter", "Newsletter"],
];

const btnBase = "min-h-[44px] min-w-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.12em] uppercase px-3 py-2 border transition-colors disabled:opacity-50";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export function RequestsPanel() {
  const [view, setView] = useState("inbox"); // inbox | trash
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({ type: "", status: "", q: "", date_from: "", date_to: "", archived: "" });
  const [trashDays, setTrashDays] = useState(30);
  const [detail, setDetail] = useState(null); // {item, normalized, linked}
  const [trashConfirm, setTrashConfirm] = useState(null); // items da cestinare
  const [permConfirm, setPermConfirm] = useState(null); // {items, preview}
  const [cancelDialog, setCancelDialog] = useState(null); // booking da annullare
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === "trash") {
        const { data } = await api.get("/admin/requests/trash");
        setItems(data.items); setTrashDays(data.trash_after_days);
      } else {
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
        const { data } = await api.get("/admin/requests", { params });
        setItems(data.items);
      }
      setSelected([]);
    } catch { toast.error("Errore nel caricamento delle richieste"); }
    finally { setLoading(false); }
  }, [view, filters]);
  useEffect(() => { load(); }, [load]);

  const key = (i) => `${i.type}:${i.id}`;
  const toggle = (i) => setSelected((s) => s.find((x) => key(x) === key(i)) ? s.filter((x) => key(x) !== key(i)) : [...s, i]);
  const allChecked = items.length > 0 && selected.length === items.length;

  const openDetail = async (i) => {
    try {
      const { data } = await api.get(`/admin/requests/${i.type}/${i.id}`);
      setDetail(data);
    } catch { toast.error("Elemento non trovato"); }
  };

  const doTrash = async (list) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/requests/trash", { items: list.map((i) => ({ type: i.type, id: i.id })) });
      toast.success(`${data.moved} element${data.moved === 1 ? "o spostato" : "i spostati"} nel Cestino`);
      setTrashConfirm(null); setDetail(null); load();
    } catch { toast.error("Errore durante lo spostamento"); }
    finally { setBusy(false); }
  };

  const doRestore = async (list) => {
    setBusy(true);
    try {
      await api.post("/admin/requests/restore", { items: list.map((i) => ({ type: i.type, id: i.id })) });
      toast.success("Elementi ripristinati");
      load();
    } catch { toast.error("Errore durante il ripristino"); }
    finally { setBusy(false); }
  };

  const doArchive = async (list, archived = true) => {
    setBusy(true);
    try {
      await api.post(`/admin/requests/archive?archived=${archived}`, { items: list.map((i) => ({ type: i.type, id: i.id })) });
      toast.success(archived ? "Elementi archiviati" : "Elementi disarchiviati");
      load();
    } catch { toast.error("Errore durante l'archiviazione"); }
    finally { setBusy(false); }
  };

  const openPermanent = async (list) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/requests/permanent/preview", { items: list.map((i) => ({ type: i.type, id: i.id })) });
      setPermConfirm({ items: list, preview: data.items });
    } catch { toast.error("Errore nella verifica dei collegamenti"); }
    finally { setBusy(false); }
  };

  const doPermanent = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/requests/permanent", { items: permConfirm.items.map((i) => ({ type: i.type, id: i.id })) });
      const anon = data.results.filter((r) => r.action === "anonymized").length;
      const del = data.results.filter((r) => r.action === "deleted").length;
      const blocked = data.results.filter((r) => r.action === "blocked").length;
      toast.success(`${del} eliminat${del === 1 ? "o" : "i"} definitivamente${anon ? `, ${anon} anonimizzat${anon === 1 ? "o" : "i"} (dati fiscali conservati)` : ""}${blocked ? `, ${blocked} bloccat${blocked === 1 ? "o" : "i"}` : ""}`);
      setPermConfirm(null); load();
    } catch { toast.error("Errore durante l'eliminazione definitiva"); }
    finally { setBusy(false); }
  };

  const doCancelBooking = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${cancelDialog.id}/cancel-with-options`, {
        cancel_gcal: cancelDialog.cancel_gcal, notify_client: cancelDialog.notify_client,
      });
      toast.success(`Prenotazione annullata${data.gcal_cancelled ? " · evento Calendar annullato" : ""}${data.client_notified ? " · cliente avvisato" : ""}`);
      setCancelDialog(null); setDetail(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore annullamento"); }
    finally { setBusy(false); }
  };

  const statuses = useMemo(() => Array.from(new Set(items.map((i) => i.status).filter(Boolean))), [items]);

  return (
    <div className="space-y-5" data-testid="requests-panel">
      {/* Vista + filtri */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setView("inbox")} className={`${btnBase} ${view === "inbox" ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`} data-testid="requests-view-inbox">
          <Inbox size={14} /> Richieste
        </button>
        <button onClick={() => setView("trash")} className={`${btnBase} ${view === "trash" ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`} data-testid="requests-view-trash">
          <Trash2 size={14} /> Cestino
        </button>
      </div>

      {view === "trash" && (
        <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex gap-3" data-testid="trash-info">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>Gli elementi nel Cestino vengono <strong>eliminati automaticamente dopo {trashDays} giorni</strong>. Le richieste collegate a pagamenti non vengono mai cancellate: i dati personali vengono anonimizzati e i riferimenti fiscali conservati.</span>
        </div>
      )}

      {view === "inbox" && (
        <div className="bg-white border border-hair p-4 grid gap-3 md:grid-cols-5" data-testid="requests-filters">
          <div className="md:col-span-2">
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Cerca (nome, email, riferimento)</Label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-taupe" />
              <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="rounded-none pl-9" data-testid="requests-search" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Tipologia</Label>
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="mt-1 w-full border border-hair bg-white px-3 h-10 text-sm" data-testid="requests-filter-type">
              {TYPE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Stato</Label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="mt-1 w-full border border-hair bg-white px-3 h-10 text-sm" data-testid="requests-filter-status">
              <option value="">Tutti</option>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Da</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="rounded-none mt-1" data-testid="requests-filter-from" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">A</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="rounded-none mt-1" data-testid="requests-filter-to" />
            </div>
          </div>
        </div>
      )}

      {/* Azioni multiple */}
      {selected.length > 0 && (
        <div className="bg-bone border border-hair p-3 flex flex-wrap items-center gap-2" data-testid="requests-bulk-bar">
          <span className="text-xs text-taupe mr-2">{selected.length} selezionat{selected.length === 1 ? "a" : "e"}</span>
          {view === "inbox" ? (
            <>
              <button onClick={() => doArchive(selected, true)} disabled={busy} className={`${btnBase} border-ink hover:bg-ink hover:text-bone`} data-testid="bulk-archive"><Archive size={14} /> Archivia</button>
              <button onClick={() => setTrashConfirm(selected)} disabled={busy} className={`${btnBase} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="bulk-trash"><Trash2 size={14} /> Elimina selezionati</button>
            </>
          ) : (
            <>
              <button onClick={() => doRestore(selected)} disabled={busy} className={`${btnBase} border-ink hover:bg-ink hover:text-bone`} data-testid="bulk-restore"><RotateCcw size={14} /> Ripristina</button>
              <button onClick={() => openPermanent(selected)} disabled={busy} className={`${btnBase} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="bulk-delete"><Trash2 size={14} /> Elimina definitivamente</button>
            </>
          )}
        </div>
      )}

      {/* Lista */}
      <div className="bg-white border border-hair" data-testid="requests-list">
        {loading && <div className="p-10 text-center text-taupe text-sm">Caricamento…</div>}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-taupe text-sm">{view === "trash" ? "Il Cestino è vuoto." : "Nessuna richiesta trovata."}</div>
        )}
        {!loading && items.length > 0 && (
          <>
            <label className="flex items-center gap-3 p-4 border-b border-hair text-xs text-taupe cursor-pointer min-h-[44px]">
              <input type="checkbox" checked={allChecked} onChange={() => setSelected(allChecked ? [] : items)} data-testid="requests-select-all" />
              Seleziona tutto ({items.length})
            </label>
            <ul className="divide-y divide-hair">
              {items.map((i) => (
                <li key={key(i)} className="p-4 flex flex-wrap items-center gap-3" data-testid={`request-row-${i.type}-${i.id}`}>
                  <input type="checkbox" checked={!!selected.find((x) => key(x) === key(i))} onChange={() => toggle(i)} className="w-5 h-5 shrink-0" data-testid={`request-check-${i.type}-${i.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{i.name || i.email}</span>
                      <span className="text-[10px] tracking-[0.12em] uppercase bg-bone px-2 py-0.5">{i.type_label}</span>
                      {i.archived && <span className="text-[10px] tracking-[0.12em] uppercase text-taupe">Archiviata</span>}
                      {view === "trash" && <span className="text-[10px] tracking-[0.12em] uppercase text-amber-700" data-testid={`trash-days-${i.id}`}>Auto-eliminazione tra {i.days_left} gg</span>}
                    </div>
                    <div className="text-xs text-taupe mt-1 truncate">{i.email}{i.summary ? ` · ${i.summary}` : ""}</div>
                    <div className="text-[11px] text-taupe mt-0.5">{fmtDate(i.date)}{i.status_label ? ` · ${i.status_label}` : ""} · rif. {i.reference}</div>
                    {view === "trash" && i.linked?.length > 0 && (
                      <div className="text-[11px] text-amber-700 mt-1">Collegamenti: {i.linked.join(", ")}</div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => openDetail(i)} className={`${btnBase} border-hair hover:border-ink`} data-testid={`request-view-${i.type}-${i.id}`}><Eye size={14} /> Visualizza</button>
                    {view === "inbox" ? (
                      <>
                        <button onClick={() => doArchive([i], !i.archived)} disabled={busy} className={`${btnBase} border-hair hover:border-ink`} data-testid={`request-archive-${i.type}-${i.id}`}><Archive size={14} /> {i.archived ? "Disarchivia" : "Archivia"}</button>
                        <button onClick={() => setTrashConfirm([i])} disabled={busy} className={`${btnBase} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`request-trash-${i.type}-${i.id}`}><Trash2 size={14} /> Elimina</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => doRestore([i])} disabled={busy} className={`${btnBase} border-ink hover:bg-ink hover:text-bone`} data-testid={`request-restore-${i.type}-${i.id}`}><RotateCcw size={14} /> Ripristina</button>
                        <button onClick={() => openPermanent([i])} disabled={busy} className={`${btnBase} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`request-delete-${i.type}-${i.id}`}><Trash2 size={14} /> Elimina definitivamente</button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Dialog dettaglio */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="request-detail-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{detail?.normalized?.type_label}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              {detail.linked.linked.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-3 text-amber-900 text-xs flex gap-2" data-testid="detail-linked">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>Dati collegati: {detail.linked.linked.join(", ")}.</span>
                </div>
              )}
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                {Object.entries(detail.item).filter(([k]) => !["history", "versions", "sent_log", "replies"].includes(k)).slice(0, 30).map(([k, v]) => (
                  <div key={k} className="flex flex-col border-b border-hair pb-1">
                    <dt className="text-[10px] tracking-[0.12em] uppercase text-taupe">{k}</dt>
                    <dd className="break-words text-xs mt-0.5">{typeof v === "object" ? JSON.stringify(v).slice(0, 160) : String(v ?? "—")}</dd>
                  </div>
                ))}
              </dl>
              {detail.normalized.type === "booking" && detail.item.status === "confirmed" && (
                <button onClick={() => setCancelDialog({ id: detail.item.id, cancel_gcal: !!detail.item.gcal_event_id, notify_client: false })}
                  className={`${btnBase} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="detail-cancel-booking">
                  <CalendarX size={14} /> Annulla prenotazione
                </button>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={() => setTrashConfirm([detail.normalized])} className={`${btnBase} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="detail-trash">
                  <Trash2 size={14} /> Sposta nel Cestino
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Conferma spostamento nel Cestino */}
      <Dialog open={!!trashConfirm} onOpenChange={(o) => !o && setTrashConfirm(null)}>
        <DialogContent className="max-w-md rounded-none" data-testid="trash-confirm-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Spostare nel Cestino?</DialogTitle></DialogHeader>
          <p className="text-sm text-taupe">Vuoi spostare questa richiesta nel Cestino? Potrai ripristinarla entro {trashDays} giorni.</p>
          <ul className="text-sm border border-hair divide-y divide-hair max-h-40 overflow-y-auto">
            {(trashConfirm || []).map((i) => (
              <li key={key(i)} className="p-3"><strong>{i.name || i.email}</strong><span className="block text-xs text-taupe">{i.type_label} · rif. {i.reference}</span></li>
            ))}
          </ul>
          <div className="flex gap-2 mt-2">
            <button onClick={() => doTrash(trashConfirm)} disabled={busy} className={`${btnBase} flex-1 bg-red-700 border-red-700 text-bone hover:opacity-90`} data-testid="trash-confirm-yes">
              {busy ? "Attendere…" : "Sì, sposta nel Cestino"}
            </button>
            <button onClick={() => setTrashConfirm(null)} className={`${btnBase} flex-1 border-hair hover:border-ink`} data-testid="trash-confirm-no">Annulla</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conferma eliminazione definitiva con avvisi collegamenti */}
      <Dialog open={!!permConfirm} onOpenChange={(o) => !o && setPermConfirm(null)}>
        <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto" data-testid="perm-confirm-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-red-800">Eliminazione definitiva</DialogTitle></DialogHeader>
          <p className="text-sm text-red-900 font-medium">Questa operazione è irreversibile. Vuoi eliminare definitivamente questa richiesta e i relativi dati personali?</p>
          <ul className="text-sm border border-hair divide-y divide-hair max-h-56 overflow-y-auto">
            {(permConfirm?.preview || []).map((p) => (
              <li key={`${p.type}:${p.id}`} className="p-3">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 ${p.action === "delete" ? "bg-red-100 text-red-800" : p.action === "anonymize" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600"}`} data-testid={`perm-action-${p.id}`}>
                    {p.action === "delete" ? "Eliminazione" : p.action === "anonymize" ? "Anonimizzazione" : "Bloccata"}
                  </span>
                  <span className="text-xs text-taupe truncate">{p.label || p.id}</span>
                </div>
                {p.warnings.map((w, idx) => <p key={idx} className="text-xs text-amber-800 mt-1">⚠ {w}</p>)}
              </li>
            ))}
          </ul>
          <div className="flex gap-2 mt-2">
            <button onClick={doPermanent} disabled={busy} className={`${btnBase} flex-1 bg-red-700 border-red-700 text-bone hover:opacity-90`} data-testid="perm-confirm-yes">
              {busy ? "Attendere…" : "Sì, elimina definitivamente"}
            </button>
            <button onClick={() => setPermConfirm(null)} className={`${btnBase} flex-1 border-hair hover:border-ink`} data-testid="perm-confirm-no">Annulla</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Annullamento prenotazione confermata */}
      <Dialog open={!!cancelDialog} onOpenChange={(o) => !o && setCancelDialog(null)}>
        <DialogContent className="max-w-md rounded-none" data-testid="cancel-booking-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Annulla prenotazione</DialogTitle></DialogHeader>
          <p className="text-sm text-taupe">Lo stato della prenotazione passerà ad Annullata. Lo storico del pagamento non verrà toccato.</p>
          <label className="flex items-center gap-3 text-sm min-h-[44px] cursor-pointer">
            <input type="checkbox" className="w-5 h-5" checked={cancelDialog?.cancel_gcal || false}
              onChange={(e) => setCancelDialog({ ...cancelDialog, cancel_gcal: e.target.checked })} data-testid="cancel-opt-gcal" />
            Annulla anche l'evento Google Calendar
          </label>
          <label className="flex items-center gap-3 text-sm min-h-[44px] cursor-pointer">
            <input type="checkbox" className="w-5 h-5" checked={cancelDialog?.notify_client || false}
              onChange={(e) => setCancelDialog({ ...cancelDialog, notify_client: e.target.checked })} data-testid="cancel-opt-email" />
            Invia e-mail di annullamento al cliente
          </label>
          <div className="flex gap-2 mt-2">
            <button onClick={doCancelBooking} disabled={busy} className={`${btnBase} flex-1 bg-red-700 border-red-700 text-bone`} data-testid="cancel-booking-yes">
              {busy ? "Attendere…" : "Conferma annullamento"}
            </button>
            <button onClick={() => setCancelDialog(null)} className={`${btnBase} flex-1 border-hair`} data-testid="cancel-booking-no">Indietro</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
